/**
 * Global UI type-scale knob (BL-77).
 *
 * ONE source of truth for the app-wide font-scale multiplier consumed by both
 * worlds:
 *   - DOM text resolves `calc(<px>px * var(--ui-scale, 1))` (see `uiTokens`),
 *     where `--ui-scale` is the CSS variable this module mirrors onto the stage
 *     root (`#game-container`). index.html also sets it statically to `1` so the
 *     DOM has a value before any JS runs (no first-paint flash).
 *   - Canvas text reads {@link getUiScale} directly (CSS variables don't reach a
 *     2D context) — see `rendering/canvasType.ts`.
 *
 * Ships **dormant at 1.0** (byte-identical to "just the new sizes"). It is the
 * architectural seam that lets the whole app's typography be nudged with a
 * single value later (desktop comfort, accessibility, per-device tiers) without
 * touching any feature module. No device logic lives here yet — that is a
 * separate, future BL.
 */

/**
 * S146 / BL-142 — the canonical "Large" presentation scale. The owner shipped
 * the prescreen text-size as **L only** (the S/M steps + their control are gone),
 * and S142's L-layout is the canonical layout, so the whole app now runs at L:
 * `getDefaultMetaState().settings.uiScale` is seeded here, the v5→v6 MetaState
 * migration normalizes any persisted S/M (0.85 / 1.0) up to it, and PreRun keeps
 * its L reflow always-on. ONE source for the value.
 */
export const L_UI_SCALE = 1.25;

let scale = 1;

/** The current global type-scale multiplier (default 1). */
export function getUiScale(): number {
  return scale;
}

/**
 * Set the global type-scale multiplier. Updates the JS source (for canvas) AND
 * mirrors it onto `--ui-scale` on `#game-container` (for DOM). Values ≤ 0 are
 * ignored (guards a transient/NaN from desyncing the two worlds).
 */
export function setUiScale(next: number): void {
  if (!(next > 0) || !Number.isFinite(next)) return;
  scale = next;
  if (typeof document !== "undefined") {
    const root = document.getElementById("game-container");
    root?.style.setProperty("--ui-scale", String(scale));
  }
}
